import { isNil } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import {
  handleResponseErrorMessage,
  useBackupCodesList,
  useInitialize2FASetup,
} from 'shared/api';
import type { Initialize2FASetupResponse } from 'shared/api/auth/types';
import { PasswordVerification } from 'src/auth/two-factor-authentication';
import { SecurityView, SetupFlowMode } from '../enums';
import { BackupCodesView } from './backup-codes-view';
import { SecurityConfigured } from './security-configured';
import { SecurityNotConfigured } from './security-not-configured';
import { SetupFlowView } from './setup-flow-view';
import { GlobalContext } from 'src/shared/context';

export const SecurityTab: React.FC = () => {
  // Contexts.
  const { loggedUser, setLoggedUser } = useContext(GlobalContext);

  // States.
  const [securityView, setSecurityView] = useState<SecurityView>(
    SecurityView.Main,
  );
  const [elevatedToken, setElevatedToken] = useState<string>('');
  const [shouldFetchCodes, setShouldFetchCodes] = useState(false);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [setupFlowMode, setSetupFlowMode] = useState<SetupFlowMode>(
    SetupFlowMode.ChangeDevice,
  );
  const [setupData, setSetupData] = useState<Initialize2FASetupResponse | null>(
    null,
  );

  // Apis.
  const {
    data: backupCodesResponse,
    isSuccess: isBackupCodesFetchSuccess,
    isFetching: isFetchingBackupCodes,
    isError: isErrorBackupCodesFetchError,
    error: backupCodesFetchError,
  } = useBackupCodesList(elevatedToken, shouldFetchCodes);

  const {
    mutate: initialize2FASetup,
    data: initializeSetupData,
    isSuccess: isInitializeSetupSuccess,
    isPending: isInitializingSetup,
    isError: isInitializeSetupError,
    error: initializeSetupError,
  } = useInitialize2FASetup();

  // UseEffects.
  useEffect(() => {
    if (isBackupCodesFetchSuccess && backupCodesResponse) {
      setBackupCodes(backupCodesResponse.data.backupCodes);
      setSecurityView(SecurityView.BackupCodes);
    }
    setShouldFetchCodes(false);
    handleResponseErrorMessage(
      isErrorBackupCodesFetchError,
      backupCodesFetchError,
    );
  }, [
    isBackupCodesFetchSuccess,
    backupCodesResponse,
    isErrorBackupCodesFetchError,
    backupCodesFetchError,
  ]);

  useEffect(() => {
    if (isInitializeSetupSuccess && initializeSetupData) {
      setSetupData(initializeSetupData.data);
      setSecurityView(SecurityView.SetupFlow);
    }
    handleResponseErrorMessage(isInitializeSetupError, initializeSetupError);
  }, [
    isInitializeSetupSuccess,
    initializeSetupData,
    isInitializeSetupError,
    initializeSetupError,
  ]);

  // Handlers.
  const handleSetupFlowSuccess = (newBackupCodes?: string[]) => {
    if (!isNil(newBackupCodes)) {
      setBackupCodes(newBackupCodes);
      setSecurityView(SecurityView.BackupCodes);
      setLoggedUser((prev) => {
        if (isNil(prev)) {
          return prev;
        }
        return { ...prev, is2FAConfigured: true };
      });
    } else {
      setSecurityView(SecurityView.Main);
    }
  };

  // Renders.
  const renderContent = () => {
    switch (securityView) {
      case SecurityView.Main:
        return loggedUser?.is2FAConfigured ? (
          <SecurityConfigured
            onViewBackupCodes={() =>
              setSecurityView(SecurityView.PasswordForBackupCodes)
            }
            onChangeAuthApp={() =>
              setSecurityView(SecurityView.PasswordForChangeDevice)
            }
          />
        ) : (
          <SecurityNotConfigured
            isLoading={isInitializingSetup}
            onConfigure={() => {
              setSetupFlowMode(SetupFlowMode.Configure);
              initialize2FASetup({});
            }}
          />
        );

      case SecurityView.PasswordForBackupCodes:
        return (
          <PasswordVerification
            backLabel="Backup Codes"
            description="For security purposes, please enter your password to view your backup codes."
            isLoading={isFetchingBackupCodes}
            onBack={() => setSecurityView(SecurityView.Main)}
            onSuccess={(token) => {
              setElevatedToken(token);
              setShouldFetchCodes(true);
            }}
          />
        );

      case SecurityView.PasswordForChangeDevice:
        return (
          <PasswordVerification
            backLabel="Change Authenticator App"
            description="For security purposes, please enter your password to change your authentication app."
            isLoading={isInitializingSetup}
            onBack={() => setSecurityView(SecurityView.Main)}
            onSuccess={(token) => {
              setElevatedToken(token);
              initialize2FASetup({
                elevatedToken: token,
              });
              setSetupFlowMode(SetupFlowMode.ChangeDevice);
            }}
          />
        );

      case SecurityView.BackupCodes:
        return (
          <BackupCodesView
            codes={backupCodes}
            mode={setupFlowMode}
            elevatedToken={elevatedToken}
            onBack={() => setSecurityView(SecurityView.Main)}
            onFinish={() => setSecurityView(SecurityView.Main)}
          />
        );

      case SecurityView.SetupFlow:
        if (isNil(setupData)) {
          return null;
        }

        return (
          <SetupFlowView
            mode={setupFlowMode}
            elevatedToken={elevatedToken}
            setupData={setupData}
            onBack={() => setSecurityView(SecurityView.Main)}
            onSuccess={handleSetupFlowSuccess}
          />
        );

      default:
        return null;
    }
  };

  return renderContent();
};
